<template>
  <div class="portfolio-filters">
    <div class="portfolio-filters__bar" @click="toggle">
      <b-icon :icon="expanded ? 'chevron-down' : 'chevron-right'" class="mr-2" font-scale="0.9"></b-icon>
      <b-icon icon="funnel" class="mr-2" font-scale="0.9"></b-icon>
      <b>Фильтры</b>
      <b-badge v-if="activeCount > 0" variant="primary" pill class="ml-2">{{ activeCount }}</b-badge>

      <!-- В свёрнутом виде - что сейчас выбрано -->
      <div v-if="!expanded" class="portfolio-filters__summary">
        <span v-for="item in summaryItems" :key="item" class="portfolio-filters__chip">{{ item }}</span>
      </div>

      <b-button
        v-if="activeCount > 0"
        variant="link"
        size="sm"
        class="ml-auto p-0"
        @click.stop="resetAll"
      >
        Сбросить
      </b-button>
    </div>

    <b-collapse :visible="expanded">
      <div class="portfolio-filters__row">
        <div class="portfolio-filters__field portfolio-filters__field--product">
          <label class="portfolio-filters__label">
            Продукт
            <b-icon
              v-b-tooltip.hover
              title="Показываются клиенты, у которых были поставки с выбранной услугой. Счётчики заказов - только по этой услуге."
              icon="info-circle"
              font-scale="0.85"
              class="ml-1 text-secondary"
            ></b-icon>
          </label>
          <b-form-select
            :value="serviceId"
            :options="serviceOptions"
            size="sm"
            @change="setService"
          ></b-form-select>
        </div>

        <div class="portfolio-filters__field portfolio-filters__field--period">
          <label class="portfolio-filters__label">
            Дата последней заявки
            <b-icon
              v-b-tooltip.hover
              title="Клиенты без заявок при выбранном периоде не показываются"
              icon="info-circle"
              font-scale="0.85"
              class="ml-1 text-secondary"
            ></b-icon>
          </label>
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="local.dateFrom"
              type="date"
              size="sm"
              :max="local.dateTo || null"
              @change="applyFilters"
            ></b-form-input>
            <span class="mx-1 text-secondary">-</span>
            <b-form-input
              v-model="local.dateTo"
              type="date"
              size="sm"
              :min="local.dateFrom || null"
              @change="applyFilters"
            ></b-form-input>
          </div>
        </div>

        <div class="portfolio-filters__field portfolio-filters__field--select">
          <label class="portfolio-filters__label">
            КАМ
            <b-icon
              v-b-tooltip.hover
              title="Менеджер ВЭД или менеджер ТЭО клиента"
              icon="info-circle"
              font-scale="0.85"
              class="ml-1 text-secondary"
            ></b-icon>
          </label>
          <multiselect
            v-model="local.kams"
            class="portfolio-filters__multiselect"
            :options="kams"
            :searchable="true"
            :close-on-select="false"
            :show-labels="false"
            :loading="managersLoading"
            :limit="1"
            :limit-text="limitText"
            multiple
            label="text"
            track-by="value"
            placeholder="Все КАМы"
            @input="applyFiltersDebounced"
          >
            <span slot="noResult">Ничего не найдено</span>
            <span slot="noOptions">Список пуст</span>
          </multiselect>
        </div>

        <div class="portfolio-filters__field portfolio-filters__field--select">
          <label class="portfolio-filters__label">Sales менеджер</label>
          <multiselect
            v-model="local.salesManagers"
            class="portfolio-filters__multiselect"
            :options="salesManagers"
            :searchable="true"
            :close-on-select="false"
            :show-labels="false"
            :loading="managersLoading"
            :limit="1"
            :limit-text="limitText"
            multiple
            label="text"
            track-by="value"
            placeholder="Все Sales менеджеры"
            @input="applyFiltersDebounced"
          >
            <span slot="noResult">Ничего не найдено</span>
            <span slot="noOptions">Список пуст</span>
          </multiselect>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { API_REFERENCE, API_CLIENTS_PORTFOLIO_MANAGERS } from "@/constants/api";
import debounce from "../../services/helpers";

// Справочник услуг заказа (references.parent_id = 21); 66 скрыта, как в аналитике продаж
const SERVICES_PARENT_ID = 21;
const EXCLUDED_SERVICE_IDS = [66];

const EXPANDED_STORAGE_KEY = "clientPortfolio.filtersExpanded";

// Ответ на устаревший запрос списков менеджеров отбрасывается
let managersRequestId = 0;

function formatDate(value) {
  if (!value) {
    return "";
  }
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
}

export default {
  data() {
    return {
      expanded: localStorage.getItem(EXPANDED_STORAGE_KEY) === "1",
      services: [],
      kams: [],
      salesManagers: [],
      managersLoading: false,
      local: {
        dateFrom: null,
        dateTo: null,
        kams: [],
        salesManagers: [],
      },
    };
  },

  computed: {
    ...mapState("clientPortfolio", ["serviceId", "dateFrom", "dateTo", "kamIds", "salesManagerIds"]),

    // Ключ текущих применённых фильтров: при его смене списки менеджеров пересчитываются
    filtersKey() {
      return JSON.stringify([this.serviceId, this.dateFrom, this.dateTo, this.kamIds, this.salesManagerIds]);
    },

    serviceOptions() {
      return [{ value: null, text: "Все продукты" }, ...this.services];
    },

    activeCount() {
      return [
        this.serviceId,
        this.dateFrom || this.dateTo,
        this.kamIds.length,
        this.salesManagerIds.length,
      ].filter(Boolean).length;
    },

    summaryItems() {
      const items = [];
      const service = this.services.find((item) => item.value === this.serviceId);
      items.push(service ? service.text : "Все продукты");

      if (this.dateFrom || this.dateTo) {
        const from = this.dateFrom ? `с ${formatDate(this.dateFrom)}` : "";
        const to = this.dateTo ? `по ${formatDate(this.dateTo)}` : "";
        items.push(`Заявка ${[from, to].filter(Boolean).join(" ")}`);
      }
      if (this.kamIds.length) {
        items.push(this.namesLabel("КАМ", this.kams, this.kamIds));
      }
      if (this.salesManagerIds.length) {
        items.push(this.namesLabel("Sales", this.salesManagers, this.salesManagerIds));
      }

      return items;
    },
  },

  methods: {
    ...mapActions("clientPortfolio", ["setService", "setFilters"]),

    toggle() {
      this.expanded = !this.expanded;
      localStorage.setItem(EXPANDED_STORAGE_KEY, this.expanded ? "1" : "0");
    },

    limitText(count) {
      return `+ ещё ${count}`;
    },

    namesLabel(title, users, ids) {
      const first = users.find((user) => user.value === ids[0]);
      const name = first ? first.text : "";
      return ids.length > 1 ? `${title}: ${name} + ещё ${ids.length - 1}` : `${title}: ${name}`;
    },

    async getServices() {
      const { data } = await axios.get(API_REFERENCE, {
        params: { reference_parent_id: SERVICES_PARENT_ID },
      });
      this.services = data.filter((service) => !EXCLUDED_SERVICE_IDS.includes(service.value));
    },

    // КАМ и Sales менеджеры только клиентов, попавших в выборку при текущих фильтрах
    async getManagers({ restoreSelection = false } = {}) {
      const requestId = ++managersRequestId;
      this.managersLoading = true;
      try {
        const { data } = await axios.get(API_CLIENTS_PORTFOLIO_MANAGERS, {
          params: {
            service_id: this.serviceId || undefined,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
            kam_ids: this.kamIds.length ? this.kamIds : undefined,
            sales_manager_ids: this.salesManagerIds.length ? this.salesManagerIds : undefined,
          },
        });
        if (requestId !== managersRequestId) {
          return;
        }
        this.kams = data.kams;
        this.salesManagers = data.sales_managers;
        // Восстановить выбор, сохранённый в сторе с прошлого визита
        if (restoreSelection) {
          this.local.kams = this.kams.filter((user) => this.kamIds.includes(user.value));
          this.local.salesManagers = this.salesManagers.filter((user) => this.salesManagerIds.includes(user.value));
        }
      } finally {
        if (requestId === managersRequestId) {
          this.managersLoading = false;
        }
      }
    },

    applyFilters() {
      const filters = {
        dateFrom: this.local.dateFrom || null,
        dateTo: this.local.dateTo || null,
        kamIds: this.local.kams.map((user) => user.value),
        salesManagerIds: this.local.salesManagers.map((user) => user.value),
      };

      const unchanged = filters.dateFrom === this.dateFrom
        && filters.dateTo === this.dateTo
        && filters.kamIds.join() === this.kamIds.join()
        && filters.salesManagerIds.join() === this.salesManagerIds.join();
      if (!unchanged) {
        this.setFilters(filters);
      }
    },

    // Выбор нескольких менеджеров подряд - один запрос
    applyFiltersDebounced: debounce(function () {
      this.applyFilters();
    }, 600),

    // Сбросить всё, включая продукт - одним пересчётом
    resetAll() {
      this.local = { dateFrom: null, dateTo: null, kams: [], salesManagers: [] };
      this.$store.commit("clientPortfolio/SET_SERVICE", null);
      this.setFilters({ dateFrom: null, dateTo: null, kamIds: [], salesManagerIds: [] });
    },
  },

  watch: {
    filtersKey() {
      this.getManagers();
    },
  },

  mounted() {
    this.local.dateFrom = this.dateFrom;
    this.local.dateTo = this.dateTo;
    this.getServices();
    this.getManagers({ restoreSelection: true });
  },
};
</script>

<style lang="scss">
.portfolio-filters {
  &__bar {
    display: flex;
    align-items: center;
    min-height: 24px;
    cursor: pointer;
    user-select: none;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: 16px;
    overflow: hidden;
  }

  &__chip {
    font-size: 12px;
    line-height: 1;
    padding: 5px 10px;
    border-radius: 12px;
    background: #f1f3f5;
    color: #555;
    white-space: nowrap;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 16px;
    padding-top: 12px;
  }

  &__field {
    &--product {
      flex: 0 0 200px;
    }
    &--period {
      flex: 0 0 300px;
    }
    &--select {
      flex: 1 1 220px;
      min-width: 220px;
    }
  }

  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
  }

  // Мультиселект в размер инпутов size="sm"
  &__multiselect.multiselect {
    min-height: 31px;

    .multiselect__select {
      height: 29px;
      top: 1px;
    }

    .multiselect__tags {
      min-height: 31px;
      padding: 4px 36px 0 8px;
      font-size: 13px;
    }

    .multiselect__placeholder,
    .multiselect__single {
      margin: 0 0 4px;
      padding-top: 1px;
      min-height: auto;
      font-size: 13px;
    }

    .multiselect__input {
      min-height: auto;
      margin: 0 0 4px;
      padding: 1px 0 0;
      font-size: 13px;
    }

    .multiselect__tag {
      margin: 0 4px 3px 0;
      padding: 3px 22px 3px 8px;
      font-size: 12px;
    }

    .multiselect__tag-icon {
      line-height: 20px;
    }

    .multiselect__strong {
      margin-bottom: 4px;
      font-size: 12px;
      line-height: 20px;
    }
  }
}
</style>
